import { type FC, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { useQuery } from "@apollo/client/react";

import { faFileWord } from "@fortawesome/pro-duotone-svg-icons/faFileWord";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  DashboardProjectsDocumentsDetailsDocument,
  DocumentKindEnum,
  MeDocument,
  RunStatusEnum,
  TranscriptStateEnum,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { ButtonAnchor } from "@styled/button_anchor";
import { Figure } from "@styled/figure";
import { Headline } from "@styled/headline";
import { HiddenPagesNotice } from "@styled/hidden_pages_notice";
import { Page } from "@styled/page";
import { PageThumbnail, PageThumbnailList } from "@styled/page_thumbnail";
import { Pane, PaneContent, PaneDrawer } from "@styled/pane";

import { AudioViewer } from "./audio_viewer";
import { DocumentDestroyButton } from "./document_destroy_button";
import { DocumentDownloadButton } from "./document_download_button";
import { ProjectDocumentPageViewer } from "./project_document_page_viewer";
import { TranscriptStateNote } from "./transcript_reader";

export const DashboardProjectsDocumentsDetails: FC = () => {
  const [params] = useSearchParams();
  const number = params.has("page") ? Number(params.get("page")) : undefined;

  const { id: projectID, documentID } = useParams<{
    id: string;
    documentID: string;
  }>();
  if (!projectID) throw new Error("undefined projectID");
  if (!documentID) throw new Error("undefined documentID");

  const navigate = useNavigate();

  const { data } = useQuery(DashboardProjectsDocumentsDetailsDocument, {
    variables: {
      projectID,
      documentID,
    },
  });

  const { data: meData } = useQuery(MeDocument);
  const isAdmin = meData?.me.role === "admin";

  const scrollToSelected = useCallback((node: HTMLAnchorElement | null) => {
    node?.scrollIntoView({ block: "center" });
  }, []);

  if (!data) return null;
  const project = data.project;
  const document = project.document;
  const pages = document.pages;

  const current = pages.find((page) => !number || page.number === number);

  // Dedup folds a page into a copy elsewhere and `Document#pages` (scoped
  // `unique_pages`) stops returning it, so the numbering below skips. The
  // server decides whether there is anything to say and writes the copy.
  const hiddenPages = document.hiddenPages;

  const documentRun = document.run;
  const isRunning = documentRun?.status === RunStatusEnum.Running;
  const hasProcessedRun =
    documentRun?.status === RunStatusEnum.Succeeded ||
    documentRun?.status === RunStatusEnum.Failed;

  // Audio recordings and dictation notes both carry a Transcript + DOCX and
  // share the transcript viewer; only audio gets the player. The server gives
  // only those a transcript state.
  const transcriptState = document.transcriptState;
  const isTranscript = transcriptState != null;

  return (
    <Page fill>
      <Headline title={`${project.name} - ${document.name}`}>
        <DocumentDownloadButton document={document} />
        {isTranscript && document.transcriptDocx && (
          <ButtonAnchor
            href={document.transcriptDocx.url}
            download
            onClick={() =>
              track("document_downloaded", {
                project_id: project.id,
                document_id: document.id,
                kind: "transcript",
              })
            }
          >
            <FontAwesomeIcon icon={faFileWord} />
            Download Word
          </ButtonAnchor>
        )}
        {isAdmin && (
          <DocumentDestroyButton
            project={project}
            document={document}
            disabled={isRunning}
            disabledReason="Processing in progress"
            cascading={hasProcessedRun}
            onDestroy={() => navigate(`/dashboard/projects/${project.id}`)}
          />
        )}
      </Headline>

      {!isTranscript && hiddenPages && (
        <HiddenPagesNotice
          summary={hiddenPages.summary}
          origins={hiddenPages.origins.map((origin) => ({
            to: `/dashboard/projects/${projectID}/documents/${origin.documentId}?page=${String(origin.pageNumber)}`,
            label: origin.label,
          }))}
        />
      )}

      <Pane fullHeight>
        {isTranscript && transcriptState !== TranscriptStateEnum.Ready ? (
          <PaneContent>
            <TranscriptStateNote state={transcriptState} />
          </PaneContent>
        ) : isTranscript ? (
          <AudioViewer
            audioUrl={
              document.kind === DocumentKindEnum.AudioRecording
                ? document.file.url
                : undefined
            }
            duration={document.transcript?.durationSeconds}
            rawText={document.transcript?.rawText}
            cleanedHtml={document.transcript?.cleanedHtml}
          />
        ) : (
          <>
            <PaneDrawer>
              <PageThumbnailList>
                {pages.map((page) => (
                  <PageThumbnail
                    key={page.id}
                    ref={current?.id === page.id ? scrollToSelected : undefined}
                    to={`/dashboard/projects/${projectID}/documents/${documentID}?page=${page.number}`}
                  >
                    <Figure
                      width={page.width ?? undefined}
                      height={page.height ?? undefined}
                      src={page.file?.url}
                      selected={current && current.id === page.id}
                    />
                    {page.number}
                  </PageThumbnail>
                ))}
              </PageThumbnailList>
            </PaneDrawer>
            <PaneContent>
              {current && (
                <ProjectDocumentPageViewer
                  projectID={projectID}
                  documentID={documentID}
                  page={current}
                />
              )}
            </PaneContent>
          </>
        )}
      </Pane>
    </Page>
  );
};
